import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { LogOut, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { AppShellSkeleton, RouteSkeleton } from './ui/feedback';
import { authStore } from '@/lib/auth-store';
import { leaveAfterSignOut } from '@/lib/session-exit';
import { announceSessionEnded } from '@/lib/use-session-timeout';
import { errorMessage } from '@/lib/api';

export function RoutePending() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return (
    <div className="p-6">
      <RouteSkeleton pathname={pathname} />
    </div>
  );
}

export function RouteError({ error }: { error: Error }) {
  const router = useRouter();
  return (
    <div className="p-6">
      <Card className="flex flex-col items-start gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-3">This page did not load</h2>
          <p className="mt-1 text-base text-muted-foreground">{errorMessage(error)}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="primary" onClick={() => void router.invalidate()}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Try again
          </Button>
          <Button asChild variant="outline">
            <Link to="/">Go to home</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function RouteNotFound() {
  return (
    <div className="p-6">
      <Card className="flex flex-col items-start gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-3">Page not found</h2>
          <p className="mt-1 text-base text-muted-foreground">
            The link may be old, or the e-file may belong to a different customer.
          </p>
        </div>
        <Button asChild variant="primary">
          <Link to="/">Go to home</Link>
        </Button>
      </Card>
    </div>
  );
}

/**
 * Shown while the API confirms who the signed-in person is. Deliberately not the app
 * skeleton: nothing that looks like the product appears until the API has said yes.
 */
export function SessionPending() {
  // The shell, with skeletons where the answers go. It was three bars breathing in the
  // middle of an empty page, which the portal never shows: reloading looked like starting
  // the application rather than returning to the page you were already on, and everything
  // then jumped into place at once.
  return <AppShellSkeleton />;
}

/** The API could not be reached or answered with an error before the app could open. */
export function SessionError({ error }: { error: Error }) {
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <Card className="flex w-full max-w-[440px] flex-col items-start gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-3">Could not open Tab Service</h2>
          <p className="mt-1 text-base text-muted-foreground">{errorMessage(error)}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="primary" onClick={() => void router.invalidate()}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Try again
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              announceSessionEnded();
              void authStore.signOut().then(() => {
                leaveAfterSignOut(() => void router.navigate({ to: '/sign-in' }));
              });
            }}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  );
}
